import React, {Fragment, ReactElement, useRef, useState} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import {useDispatch} from 'react-redux';
import {PopoverBody} from 'reactstrap';
import styled from 'styled-components';

import {UserAvatar} from 'app/components/general/avatars';
import {StyledPopoverBottomDIV} from 'app/general/bootstrap/utils';
import {FormControlGroup} from 'app/general/forms/FormControlGroup';
import {OverlayProps} from 'app/general/overlays';
import {PopoverForm} from 'app/general/overlays/PopoverForm';
import {postSignoffReminder} from 'app/signoffs/actions';
import {showNotification} from 'app/toasts/slice';
import {gt} from 'app/utils/i18n';
import {createErrorHandler} from 'app/utils/request';

import {BaseDocumentContentType, BasicUserSerializedType} from 'sweet-types';

interface Props {
  overlayProps: OverlayProps;
  contentType: BaseDocumentContentType;
  objectId: number;
  users: Array<BasicUserSerializedType>;
  onRequestSignoff: () => unknown;
}

export const SignoffIndividualUsersPopover = ({
  overlayProps,
  contentType,
  objectId,
  users,
  onRequestSignoff,
}: Props): ReactElement => {
  const refreshPopover = useRef<() => void>(() => undefined);

  const [sendingRequest, setSendingRequest] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const dispatch = useDispatch();

  const requestManualSignoff = () => {
    const user_ids = users.map((user) => user.url);

    setSendingRequest(true);

    return postSignoffReminder({
      object_id: objectId,
      content_type: contentType,
      user_ids,
      message,
    }).then(
      () => {
        setSendingRequest(false);
        onRequestSignoff();
        return dispatch(showNotification(gt('Sign Off reminder sent.')));
      },
      (error) => {
        setSendingRequest(false);
        return createErrorHandler(
          gt('Not able to request Sign Off'),
          dispatch
        )(error);
      }
    );
  };

  return (
    <PopoverForm overlayProps={overlayProps}>
      {({close, refreshPosition, closeOnSuccess}) => {
        // used to refresh the page when toggled.
        refreshPopover.current = refreshPosition;
        return (
          <Fragment>
            <PopoverHeading>{gt('Sign Off Reminder')}</PopoverHeading>
            <StyledScrollableContainerDiv className="d-flex flex-column">
              <PopoverBody>
                <p>
                  {gt('Remind the selected members that Sign Off is required.')}
                </p>
                <div className="my-2">
                  <FormControlGroup field="message">
                    <textarea
                      className="form-control form-control-sm"
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      rows={3}
                      name="message"
                      placeholder={gt('Message (optional)')}
                    />
                  </FormControlGroup>
                </div>
                <p className="mb-1">{gt('Members reminded')}</p>
                <ul className="list-unstyled border-top">
                  {users.map((user) => (
                    <li
                      key={user.id}
                      className="d-flex align-items-center py-2 border-bottom"
                    >
                      <UserAvatar user={user} small />
                      <div className="ml-2">
                        <div>{user.name}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </PopoverBody>
            </StyledScrollableContainerDiv>
            <StyledPopoverBottomDIV>
              <button
                onClick={close}
                className="btn btn-link btn-sm float-left"
              >
                <i className="fa fa-times" /> {gt('Close')}
              </button>
              <button
                onClick={() => closeOnSuccess(requestManualSignoff())}
                disabled={sendingRequest}
                className="btn btn-primary btn-sm"
              >
                {sendingRequest ? (
                  <span>
                    <i className="fa fa-circle-notch fa-spin" /> {gt('Sending')}
                  </span>
                ) : (
                  gt('Send Reminder')
                )}
              </button>
            </StyledPopoverBottomDIV>
          </Fragment>
        );
      }}
    </PopoverForm>
  );
};

const StyledScrollableContainerDiv = styled.div`
  max-height: calc(100vh - 300px);
  width: 274px;
  overflow: auto;
`;

const PopoverHeading = styled.div`
  background: ${colors.lightBg};
  letter-spacing: -0.25pt;
  font-size: 80%;
  text-transform: uppercase;
  font-weight: bold;
  padding: 0.5rem 1.25rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
`;
